import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Textarea,
  Badge,
  SimpleGrid,
  Container,
  Heading,
  useColorModeValue,
  Spinner,
  Center,
  IconButton,
  Tooltip,
  Alert,
  AlertIcon,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  useToast,
  Checkbox,
  Image,
  Avatar
} from '@chakra-ui/react';
import { 
  FiFolder, 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiUsers,
  FiRefreshCw,
  FiHeart,
  FiShare,
  FiPlay,
  FiEye
} from 'react-icons/fi';
import { ContentManagementService, type ContentCollection } from '../services/contentManagementService';
import { useAuth } from '../contexts/AuthContext';
import IconWrapper from './IconWrapper';
import { useNavigate } from 'react-router-dom';

interface ContentCollectionsProps {
  showHeader?: boolean;
  userId?: string;
}

const ContentCollections: React.FC<ContentCollectionsProps> = ({
  showHeader = true,
  userId
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [collections, setCollections] = useState<ContentCollection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<ContentCollection | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Form states
  const [collectionName, setCollectionName] = useState('');
  const [collectionDescription, setCollectionDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [isCurated, setIsCurated] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    loadCollections();
  }, [userId]);

  const loadCollections = async () => {
    try {
      setIsLoading(true);
      const collectionsData = await ContentManagementService.getCollections(userId);
      setCollections(collectionsData);
    } catch (error) {
      console.error('Error loading collections:', error);
      toast({
        title: 'Error loading collections',
        description: 'Failed to load collections. Please try again.',
        status: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCollection = async () => {
    if (!collectionName.trim()) {
      toast({
        title: 'Collection name required',
        description: 'Please enter a collection name.',
        status: 'error'
      });
      return;
    }

    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'Please sign in to create collections.',
        status: 'error'
      });
      return;
    }

    try {
      setIsCreating(true);
      await ContentManagementService.createCollection(
        collectionName,
        collectionDescription,
        user.uid,
        user.displayName || 'Unknown User',
        isPublic
      );
      
      toast({
        title: 'Collection created',
        description: 'Collection has been created successfully.',
        status: 'success'
      });
      
      setCollectionName('');
      setCollectionDescription('');
      setIsPublic(true);
      setIsCurated(false);
      await loadCollections();
    } catch (error: any) {
      console.error('Error creating collection:', error);
      toast({
        title: 'Error creating collection',
        description: error.message || 'Failed to create collection.',
        status: 'error'
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditCollection = async () => {
    if (!selectedCollection) return;

    try {
      await ContentManagementService.updateCategory(selectedCollection.id, {
        name: collectionName,
        description: collectionDescription
      });
      
      toast({
        title: 'Collection updated',
        description: 'Collection has been updated successfully.',
        status: 'success'
      });
      
      setIsEditModalOpen(false);
      setSelectedCollection(null);
      await loadCollections();
    } catch (error: any) {
      console.error('Error updating collection:', error);
      toast({
        title: 'Error updating collection',
        description: error.message || 'Failed to update collection.',
        status: 'error'
      });
    }
  };

  const handleFollowCollection = async (collectionId: string) => {
    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'Please sign in to follow collections.',
        status: 'error'
      });
      return;
    }

    try {
      await ContentManagementService.followCollection(collectionId, user.uid);
      
      toast({
        title: 'Collection followed',
        description: 'You are now following this collection.',
        status: 'success'
      });
      
      await loadCollections();
    } catch (error: any) {
      console.error('Error following collection:', error);
      toast({
        title: 'Error following collection',
        description: error.message || 'Failed to follow collection.',
        status: 'error'
      });
    }
  };

  const handleCollectionClick = (collection: ContentCollection) => {
    navigate(`/collection/${collection.id}`);
  };

  const filteredCollections = collections.filter(collection =>
    collection.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    collection.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const renderCollectionCard = (collection: ContentCollection) => {
    const isOwner = user?.uid === collection.creatorId;
    
    return (
      <Box
        key={collection.id}
        bg={cardBg}
        borderRadius="lg"
        border="1px"
        borderColor={borderColor}
        overflow="hidden"
        cursor="pointer"
        transition="all 0.2s"
        _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
        onClick={() => handleCollectionClick(collection)}
      >
        <VStack align="stretch" gap={0}>
          {/* Collection Header */}
          <Box p={6} pb={4}>
            <HStack justify="space-between" mb={3}>
              <HStack gap={3}>
                <IconWrapper icon={FiFolder} size={24} color="purple.400" />
                <VStack align="start" gap={1}>
                  <Text fontWeight="bold" fontSize="lg" color="white" noOfLines={1}>
                    {collection.name}
                  </Text>
                  <HStack gap={2}>
                    <Badge colorScheme="blue" fontSize="xs">
                      {collection.isPublic ? 'Public' : 'Private'}
                    </Badge>
                    {collection.isCurated && (
                      <Badge colorScheme="purple" fontSize="xs">
                        Curated
                      </Badge>
                    )}
                  </HStack>
                </VStack>
              </HStack>
              
              {isOwner && (
                <HStack gap={1}>
                  <Tooltip label="Edit collection">
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={<IconWrapper icon={FiEdit} size={14} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCollection(collection);
                        setCollectionName(collection.name);
                        setCollectionDescription(collection.description);
                        setIsEditModalOpen(true);
                      }}
                      aria-label="Edit collection"
                    />
                  </Tooltip>
                  <Tooltip label="Delete collection">
                    <IconButton
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      icon={<IconWrapper icon={FiTrash2} size={14} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCollection(collection);
                        setIsDeleteModalOpen(true);
                      }}
                      aria-label="Delete collection"
                    />
                  </Tooltip>
                </HStack>
              )}
            </HStack>

            <Text color="gray.400" fontSize="sm" noOfLines={2} mb={4}>
              {collection.description}
            </Text>

            <HStack justify="space-between" fontSize="sm" color="gray.500">
              <HStack>
                <IconWrapper icon={FiPlay} size={14} />
                <Text>{formatNumber(collection.contentCount)} items</Text>
              </HStack>
              <HStack>
                <IconWrapper icon={FiUsers} size={14} />
                <Text>{formatNumber(collection.followers)} followers</Text>
              </HStack>
            </HStack>
          </Box>

          {/* Collection Footer */}
          <Box bg="gray.700" p={4}>
            <HStack justify="space-between">
              <HStack gap={2}>
                <Avatar size="sm" name={collection.creatorName} />
                <Text fontSize="sm" color="gray.300">
                  by {collection.creatorName}
                </Text>
              </HStack>
              
              {!isOwner && (
                <Button
                  size="sm"
                  colorScheme="purple"
                  variant="outline"
                  leftIcon={<IconWrapper icon={FiHeart} size={14} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFollowCollection(collection.id);
                  }}
                >
                  Follow
                </Button>
              )}
            </HStack>
          </Box>
        </VStack>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Center py={8}>
        <Spinner size="lg" color="purple.400" />
      </Center>
    );
  }

  return (
    <Container maxW="container.xl">
      <VStack gap={6} align="stretch">
        {showHeader && (
          <HStack justify="space-between">
            <Heading size="lg" color="white">
              <IconWrapper icon={FiFolder} size={24} style={{ marginRight: '8px' }} />
              Content Collections
            </Heading>
            <Tooltip label="Refresh collections">
              <IconButton
                aria-label="Refresh"
                icon={<IconWrapper icon={FiRefreshCw} size={16} />}
                onClick={loadCollections}
                variant="ghost"
                colorScheme="purple"
              />
            </Tooltip>
          </HStack>
        )}

        {/* Search and Create */}
        <HStack gap={4}>
          <Input
            placeholder="Search collections..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg="gray.700"
            border="none"
            color="white"
            _placeholder={{ color: 'gray.400' }}
            _focus={{ bg: 'gray.600' }}
            flex={1}
          />
          {user && (
            <Button
              colorScheme="purple"
              leftIcon={<IconWrapper icon={FiPlus} size={16} />}
              onClick={() => setIsEditModalOpen(true)}
            >
              Create Collection
            </Button>
          )}
        </HStack>

        {/* Collections List */}
        {filteredCollections.length === 0 ? (
          <Center py={8}>
            <VStack gap={2}>
              <Text color="gray.500">
                {searchQuery ? 'No collections found matching your search' : 'No collections found'}
              </Text>
              {!searchQuery && (
                <Text fontSize="sm" color="gray.400">
                  Create your first collection to get started
                </Text>
              )}
            </VStack>
          </Center>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
            {filteredCollections.map(renderCollectionCard)}
          </SimpleGrid>
        )}

        {/* Create/Edit Collection Modal */}
        <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
          <ModalOverlay />
          <ModalContent bg="gray.800" color="white">
            <ModalHeader>
              {selectedCollection ? 'Edit Collection' : 'Create New Collection'}
            </ModalHeader>
            <ModalBody>
              <VStack gap={4}>
                <FormControl>
                  <FormLabel>Collection Name</FormLabel>
                  <Input
                    placeholder="Enter collection name"
                    value={collectionName}
                    onChange={(e) => setCollectionName(e.target.value)}
                    bg="gray.700"
                    border="none"
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{ bg: 'gray.600' }}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    placeholder="Enter collection description"
                    value={collectionDescription}
                    onChange={(e) => setCollectionDescription(e.target.value)}
                    bg="gray.700"
                    border="none"
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{ bg: 'gray.600' }}
                    rows={3}
                  />
                </FormControl>
                
                <FormControl>
                  <Checkbox
                    isChecked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                    colorScheme="purple"
                  >
                    Make collection public
                  </Checkbox>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => setIsEditModalOpen(false)}>
                Cancel
              </Button>
              <Button
                colorScheme="purple"
                onClick={selectedCollection ? handleEditCollection : handleCreateCollection}
                isLoading={isCreating}
                loadingText={selectedCollection ? "Updating..." : "Creating..."}
              >
                {selectedCollection ? 'Update' : 'Create'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
          <ModalOverlay />
          <ModalContent bg="gray.800" color="white">
            <ModalHeader>Delete Collection</ModalHeader>
            <ModalBody>
              <Alert status="warning">
                <AlertIcon />
                <AlertDescription>
                  Are you sure you want to delete this collection? This action cannot be undone.
                  All content in this collection will be removed.
                </AlertDescription>
              </Alert>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => setIsDeleteModalOpen(false)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={() => setIsDeleteModalOpen(false)}>
                Delete
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Container>
  );
};

export default ContentCollections;
